import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useModal } from '../hooks/useModal';
import { ApiService } from '../services/api';
import React, { useState } from 'react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { loading, systemConfig } = useAuth();
  const { showAlert } = useModal();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      const errorMessage = 'E-posta ve şifre alanları zorunludur';
      setError(errorMessage);
      await showAlert({
        title: 'Giriş Hatası',
        message: errorMessage,
        type: 'error',
        buttonText: 'Tamam',
      });

      return;
    };

    setSubmitting(true);
    try {
      const response = await ApiService.login({ email, password });

      if (response.requiresPhoneVerification) {
        navigate("/verify-phone", {
          state: {
            userId: response.userId,
            phone: response.phone,
            fromLogin: true,
          },
        });

        return;
      };

      if (response.token && response.user) {
        localStorage.setItem("authToken", response.token);
        localStorage.setItem("refreshToken", response.refreshToken);
        localStorage.setItem("userData", JSON.stringify(response.user));

        window.location.href = "/";
      };
    } catch (error: any) {
      console.error("Login error:", error);
      let errorMessage = "Giriş yapılırken bir hata oluştu. Lütfen bilgilerinizi kontrol edin.";

      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setError(errorMessage);

      if (typeof errorMessage === "string" && errorMessage.toLowerCase().includes("engellendiniz")) {
        await showAlert({
          title: "💳 Ödeme Yapılması Gerekiyor",
          message:
            "Hesabınız ödeme yapılmadığı için sistem yöneticisi tarafından geçici olarak engellenmiştir.\nÖdemenizi gerçekleştirdikten sonra erişiminiz yeniden açılacaktır.",
          type: "warning",
          buttonText: "Anladım",
        });
      } else {
        await showAlert({
          title: "Giriş Başarısız",
          message: errorMessage,
          type: "error",
          buttonText: "Tekrar Dene",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    if (systemConfig?.isGoogleEnabled) {
      // Implement Google OAuth
      console.log("Google login");
    } else {
      const errorMessage = "Google ile giriş şu anda aktif değil";
      setError(errorMessage);
      showAlert({
        title: "Google Giriş",
        message: errorMessage,
        type: "warning",
        buttonText: "Tamam",
      });
    }
  };

  const handleMicrosoftLogin = () => {
    // Implement Microsoft OAuth
    console.log("Microsoft login");
  };

  // Show loading spinner while system config is loading
  if (loading && !systemConfig) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mb-6 flex justify-center">
            <img
              src={systemConfig?.companyLogo || "/logo.svg"}
              alt={systemConfig?.companyName || "Logo"}
              className="h-24 w-24 object-cover rounded-full"
            />
          </div>

          <div className="flex items-center justify-center mb-2">
            <div className="text-3xl font-black tracking-tight text-gray-900">
              {systemConfig?.companyName ? (
                <span>{systemConfig.companyName}</span>
              ) : (
                <>
                  <span>Yeni Eğitim Sistemi</span>
                </>
              )}
            </div>
          </div>

          <h2 className="text-lg font-medium text-gray-500 mb-4">
            Tekrar Hoş Geldiniz! 👋
          </h2>

          <p className="text-gray-400 text-sm max-w-xs mx-auto leading-relaxed">
            {systemConfig?.companyName || "Yeni Eğitim Sistemi"} platformuna erişmek için lütfen giriş yapın.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Giriş Hatası</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="email"
              placeholder="ornek@sirketiniz.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium placeholder:text-gray-400"
              required
              disabled={submitting}
            />
          </div>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 pr-12 transition-all font-medium placeholder:text-gray-400"
              required
              disabled={submitting}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              disabled={submitting}>
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-purple-600 text-white py-3 px-4 rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all shadow-lg shadow-purple-600/20 font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Giriş yapılıyor...
              </div>
            ) : (
              "Giriş Yap"
            )}
          </button>
        </form>

        {systemConfig?.isPasswordResetEnabled !== false && (
          <div className="mt-3 text-center">
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-purple-700 hover:text-purple-800 transition-colors"
            >
              {systemConfig?.passwordResetPromptText || 'Şifremi unuttum'}
            </Link>
          </div>
        )}

        {systemConfig?.isRegistrationEnabled && (
          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-center">
            <p className="text-sm font-medium text-gray-700">
              {systemConfig?.registrationPromptText || 'Henüz hesabın yok mu?'}
            </p>
            <Link
              to="/register"
              className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-purple-200 bg-white px-4 py-2.5 font-semibold text-purple-700 transition-colors hover:border-purple-300 hover:bg-purple-50 focus:outline-none focus:ring-4 focus:ring-purple-500/15"
            >
              Kayıt Ol
            </Link>
          </div>
        )}

        {(systemConfig?.isGoogleEnabled || systemConfig?.isFacebookEnabled || systemConfig?.isAppleEnabled) && (
          <div className="mt-6">
            <div className="text-center text-gray-500 mb-4">YA DA</div>

            <div className="space-y-3">
              {systemConfig?.isGoogleEnabled && (
                <button
                  onClick={handleGoogleLogin}
                  disabled={submitting}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Google ile devam et
                </button>
              )}

              <button
                onClick={handleMicrosoftLogin}
                disabled={submitting}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#F25022" d="M0 0h11.5v11.5H0z" />
                  <path fill="#00A1F1" d="M12.5 0H24v11.5H12.5z" />
                  <path fill="#7FBA00" d="M0 12.5h11.5V24H0z" />
                  <path fill="#FFB900" d="M12.5 12.5H24V24H12.5z" />
                </svg>
                Microsoft Hesabı ile devam et
              </button>
            </div>
          </div>
        )}

        {(systemConfig?.companyEmail || systemConfig?.companyPhone || systemConfig?.companyAddress) && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="text-center text-sm text-gray-500 space-y-1">
              {systemConfig?.companyEmail && <div>E-posta: {systemConfig.companyEmail}</div>}
              {systemConfig?.companyPhone && <div>Telefon: {systemConfig.companyPhone}</div>}
              {systemConfig?.companyAddress && <div>Adres: {systemConfig.companyAddress}</div>}
            </div>
          </div>
        )}

      </div>

      <a
        href="https://wa.me/905441461000"
        target="_blank"
        rel="noreferrer"
        aria-label="WhatsApp ile İletişime Geç"
        title="WhatsApp ile İletişime Geç"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-green-500/30 transition-transform hover:scale-105 hover:bg-[#20ba5a] focus:outline-none focus:ring-4 focus:ring-green-500/30"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="h-8 w-8 fill-current"
        >
          <path d="M12 2C6.477 2 2 6.145 2 11.259c0 2.036.71 3.918 1.91 5.437L2.65 22l5.557-1.47A10.37 10.37 0 0 0 12 20.518c5.523 0 10-4.145 10-9.259C22 6.145 17.523 2 12 2Zm0 16.993a8.59 8.59 0 0 1-3.984-.978l-.284-.145-3.298.873.88-3.11-.186-.286a7.94 7.94 0 0 1-1.236-4.088c0-4.273 3.64-7.75 8.108-7.75 4.47 0 8.108 3.477 8.108 7.75 0 4.274-3.639 7.734-8.108 7.734Zm4.448-5.775c-.243-.116-1.438-.69-1.66-.769-.223-.081-.386-.116-.548.116-.162.232-.629.768-.771.926-.142.156-.284.174-.528.058-.243-.116-1.027-.36-1.956-1.15-.722-.614-1.21-1.373-1.352-1.605-.142-.232-.015-.357.107-.472.109-.104.243-.27.365-.406.122-.136.162-.232.244-.387.08-.154.04-.29-.02-.406-.061-.116-.548-1.296-.75-1.775-.198-.47-.4-.406-.548-.414-.142-.006-.304-.008-.467-.008a.897.897 0 0 0-.649.29c-.223.232-.852.807-.852 1.968s.873 2.283.994 2.438c.122.154 1.716 2.633 4.159 3.69.581.25 1.035.4 1.388.512.583.184 1.113.158 1.532.096.467-.069 1.438-.58 1.64-1.141.204-.56.204-1.04.142-1.14-.06-.098-.223-.156-.467-.272Z" />
        </svg>
      </a>
    </div>
  );
}
